import { Routes, Route, Navigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import OverviewPage from "./pages/OverviewPage";
import ConfigPage from "./pages/ConfigPage";
import ReviewPage from "./pages/ReviewPage";
import RunDetailsPage from "./pages/RunDetailsPage";
import RunsPage from "./pages/RunsPage";
import WorkOrdersPage from "./pages/WorkOrdersPage";
import AdminPage from "./pages/AdminPage";
import ProtectedRoute from "./components/ProtectedRoute";
import ControlPlaneLayout from "./api/ControlPlaneLayout";
import BrainConsoleLayout from "./components/BrainConsoleLayout";
import BrainOverviewPage from "./pages/console/OverviewPage";
import BrainRunsPage from "./pages/console/RunsPage";
import UploadPage from "./pages/console/UploadPage";
import DomainsPage from "./pages/console/DomainsPage";
import RequirementsPage from "./pages/console/RequirementsPage";
import LLMConfigPage from "./pages/console/LLMConfigPage";
import DocsPage from "./pages/console/DocsPage";
import ChatPage from "./pages/ChatPage";

// Wait for MSAL interaction to settle before redirecting (prevents loops on redirect callbacks).
const RootRedirect: React.FC = () => {
  const { inProgress } = useMsal();
  if (inProgress !== InteractionStatus.None) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600" />
      </div>
    );
  }
  return <Navigate to="/orchestration" replace />;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Everything lives under the auth-gated control plane. Overview is the
          entry page (index). ProtectedRoute gates the whole section, so auth is
          required for every route. */}
      <Route
        path="/orchestration"
        element={
          <ProtectedRoute>
            <ControlPlaneLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<OverviewPage />} />
        <Route path="work-orders" element={<WorkOrdersPage />} />
        <Route path="runs" element={<RunsPage />} />
        <Route path="runs/:id" element={<RunDetailsPage />} />
        <Route path="config" element={<ConfigPage />} />
        <Route path="review" element={<ReviewPage />} />
        <Route path="admin" element={<AdminPage />} />
      </Route>

      <Route
        path="/console"
        element={
          <ProtectedRoute>
            <BrainConsoleLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<BrainOverviewPage />} />
        <Route path="runs" element={<BrainRunsPage />} />
        <Route path="upload" element={<UploadPage />} />
        <Route path="domains" element={<DomainsPage />} />
        <Route path="requirements" element={<RequirementsPage />} />
        <Route path="config" element={<LLMConfigPage />} />
        <Route path="docs" element={<DocsPage />} />
        <Route path="chat" element={<ChatPage />} />
      </Route>

      {/* No public home — root and anything unknown go to the (protected)
          Overview (MSAL/Entra login handled by ProtectedRoute). */}
      <Route path="*" element={<RootRedirect />} />
    </Routes>
  );
};

export default AppRoutes;
